<template>
  <div class="app-container">
    <div v-if="!hasPermission" class="no-permission">
      <i class="el-icon-warning-outline"></i>
      <h2>您没有权限访问此页面</h2>
      <p>请联系系统管理员获取权限</p>
    </div>

    <template v-else>
      <el-card class="search-card" shadow="never">
        <el-form
          :model="filters"
          size="small"
          :inline="true"
          label-width="80px"
          @submit.native.prevent
        >
          <el-form-item label="配置键名" prop="configKey">
            <el-input
              v-model="filters.configKey"
              placeholder="请输入配置键名"
              clearable
              style="width: 200px;"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="配置描述" prop="configDesc">
            <el-input
              v-model="filters.configDesc"
              placeholder="请输入配置描述"
              clearable
              style="width: 200px;"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" size="mini" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" size="mini" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <el-card class="table-card" shadow="never">
        <div class="table-toolbar">
          <div class="toolbar-left">
            <el-button type="primary" plain icon="el-icon-plus" size="mini" @click="openCreateDialog">新增</el-button>
          </div>
          <div class="toolbar-right">
            <el-tooltip content="刷新" placement="top">
              <el-button circle icon="el-icon-refresh" size="mini" @click="fetchList" />
            </el-tooltip>
          </div>
        </div>
        <el-table
          v-loading="loading"
          :data="pagedList"
          border
          stripe
          size="small"
          style="width: 100%;"
          :header-cell-style="headerCellStyle"
        >
          <el-table-column type="index" label="序号" width="60" align="center" :index="indexMethod" />
          <el-table-column prop="id" label="配置编号" width="90" align="center" />
          <el-table-column prop="configKey" label="配置键名" min-width="140" show-overflow-tooltip />
          <el-table-column label="配置值" min-width="200">
            <template slot-scope="scope">
              <div v-if="isImageValue(scope.row.configValue)" class="value-image-wrap">
                <img :src="scope.row.configValue" alt="配置图" class="value-thumb" @click="previewImage(scope.row.configValue)">
              </div>
              <span v-else>{{ scope.row.configValue || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="configDesc" label="配置描述" min-width="180" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.configDesc || '—' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" icon="el-icon-edit" size="mini" @click="openEditDialog(scope.row)">修改</el-button>
              <el-button type="text" icon="el-icon-delete" size="mini" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="configList.length"
            :current-page.sync="pageNum"
            :page-size.sync="pageSize"
            :page-sizes="[10, 20, 50]"
            @size-change="pageNum = 1"
          />
        </div>
      </el-card>

      <el-dialog
        :title="dialogMode === 'create' ? '添加配置' : '修改配置'"
        :visible.sync="dialogVisible"
        width="560px"
        append-to-body
        :close-on-click-modal="false"
        @closed="resetForm"
      >
        <el-form :model="form" :rules="rules" ref="configForm" label-width="90px" size="small">
          <el-form-item label="配置键名" prop="configKey">
            <el-input v-model="form.configKey" placeholder="如 payment_qr" maxlength="50" />
          </el-form-item>
          <el-form-item label="配置值" prop="configValue">
            <el-input
              v-model="form.configValue"
              placeholder="可填文本，或下方上传图片"
              maxlength="500"
              style="margin-bottom: 10px;"
            />
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="uploadConfigImage"
              accept="image/*"
            >
              <el-button size="mini" type="primary" plain icon="el-icon-upload2" :loading="uploading">上传图片</el-button>
            </el-upload>
            <div v-if="isImageValue(form.configValue)" class="form-preview">
              <img :src="form.configValue" alt="预览">
              <el-button type="text" size="mini" class="danger-text" @click="form.configValue = ''">清除图片</el-button>
            </div>
          </el-form-item>
          <el-form-item label="配置描述" prop="configDesc">
            <el-input
              v-model="form.configDesc"
              type="textarea"
              :rows="3"
              placeholder="请输入内容"
              maxlength="255"
              show-word-limit
            />
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button type="primary" size="small" :loading="submitting" @click="handleSubmit">确 定</el-button>
          <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        </div>
      </el-dialog>

      <el-dialog title="图片预览" :visible.sync="previewVisible" width="480px" append-to-body>
        <div class="preview-dialog-body">
          <img v-if="previewUrl" :src="previewUrl" alt="预览">
        </div>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import request from '@/utils/request'

export default {
  name: 'ConfigManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      uploading: false,
      configList: [],
      filters: { configKey: '', configDesc: '' },
      pageNum: 1,
      pageSize: 10,
      dialogVisible: false,
      dialogMode: 'create',
      previewVisible: false,
      previewUrl: '',
      form: {
        id: null,
        configKey: '',
        configValue: '',
        configDesc: ''
      },
      rules: {
        configKey: [
          { required: true, message: '配置键名不能为空', trigger: 'blur' }
        ],
        configValue: [
          { required: true, message: '配置值不能为空', trigger: 'blur' }
        ]
      },
      headerCellStyle: {
        background: '#f8f8f9',
        color: '#515a6e',
        fontWeight: '600'
      }
    }
  },
  computed: {
    ...mapGetters({
      permission: 'user/permission'
    }),
    hasPermission() {
      try {
        const localStoragePermission = localStorage.getItem('user-permission')
        return localStoragePermission === '1' || this.permission === '1' || this.permission === 1
      } catch (error) {
        return false
      }
    },
    pagedList() {
      const start = (this.pageNum - 1) * this.pageSize
      return this.configList.slice(start, start + this.pageSize)
    }
  },
  created() {
    if (this.hasPermission) {
      this.fetchList()
    }
  },
  methods: {
    indexMethod(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    isImageValue(value) {
      if (!value || typeof value !== 'string') return false
      const v = value.trim()
      if (!/^https?:\/\//i.test(v)) return false
      return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(v) || /\/Order\/config\//i.test(v)
    },
    previewImage(url) {
      this.previewUrl = url
      this.previewVisible = true
    },
    async uploadConfigImage(option) {
      const file = option.file
      if (!file) return
      if (!file.type.startsWith('image/')) {
        this.$message.error('请上传图片文件')
        return
      }
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('image', file)
        const url = await request.post('/api/common/upload/config', formData)
        this.form.configValue = url
        this.$message.success('图片上传成功')
        this.$nextTick(() => {
          if (this.$refs.configForm) this.$refs.configForm.clearValidate('configValue')
        })
      } catch (error) {
        this.$message.error((error && error.message) || '图片上传失败')
      } finally {
        this.uploading = false
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.configKey) params.configKey = this.filters.configKey.trim()
        if (this.filters.configDesc) params.configDesc = this.filters.configDesc.trim()
        const data = await request.get('/api/config/list', { params })
        this.configList = Array.isArray(data) ? data : []
        this.pageNum = 1
      } catch (error) {
        this.$message.error((error && error.message) || '获取配置列表失败')
        this.configList = []
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.fetchList()
    },
    handleReset() {
      this.filters = { configKey: '', configDesc: '' }
      this.fetchList()
    },
    openCreateDialog() {
      this.dialogMode = 'create'
      this.resetForm()
      this.dialogVisible = true
    },
    openEditDialog(row) {
      this.dialogMode = 'edit'
      this.form = {
        id: row.id,
        configKey: row.configKey || '',
        configValue: row.configValue || '',
        configDesc: row.configDesc || ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.configForm) this.$refs.configForm.clearValidate()
      })
    },
    resetForm() {
      this.form = { id: null, configKey: '', configValue: '', configDesc: '' }
      this.$nextTick(() => {
        if (this.$refs.configForm) this.$refs.configForm.clearValidate()
      })
    },
    handleSubmit() {
      this.$refs.configForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            configKey: this.form.configKey.trim(),
            configValue: this.form.configValue.trim(),
            configDesc: (this.form.configDesc || '').trim()
          }
          if (this.dialogMode === 'create') {
            await request.post('/api/config', payload)
            this.$message.success('新增成功')
          } else {
            payload.id = this.form.id
            await request.put('/api/config', payload)
            this.$message.success('修改成功')
          }
          this.dialogVisible = false
          this.fetchList()
        } catch (error) {
          this.$message.error((error && error.message) || '操作失败，请重试')
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(`是否确认删除配置键名为"${row.configKey}"的数据项？`, '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await request.delete(`/api/config/${row.id}`)
          this.$message.success('删除成功')
          this.fetchList()
        } catch (error) {
          this.$message.error((error && error.message) || '删除失败，请重试')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.app-container { width: 100%; }
.no-permission {
  background: #fff;
  border-radius: 4px;
  padding: 60px 24px;
  text-align: center;
  color: #909399;
}
.no-permission i { font-size: 48px; color: #e6a23c; margin-bottom: 12px; }
.no-permission h2 { margin: 8px 0; font-size: 18px; color: #303133; font-weight: 500; }
.search-card { margin-bottom: 10px; }
.search-card >>> .el-card__body { padding: 18px 18px 0; }
.table-card >>> .el-card__body { padding: 16px; }
.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.config-tip { margin-bottom: 12px; }
.danger-text { color: #f56c6c !important; }
.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.dialog-footer { text-align: right; }
.value-image-wrap { display: flex; align-items: center; }
.value-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #ebeef5;
}
.form-preview {
  margin-top: 10px;
}
.form-preview img {
  max-width: 220px;
  max-height: 220px;
  display: block;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  margin-bottom: 4px;
}
.preview-dialog-body {
  text-align: center;
}
.preview-dialog-body img {
  max-width: 100%;
  max-height: 420px;
}
</style>
